import { keyBy } from 'lodash';
import type { Observable } from 'rxjs';
import { of, tap, map } from 'rxjs';

import { FlightEndpoint } from '@apps/main/modules/api/flight';
import { FerryEndpoint } from '@apps/main/modules/api/ferry';

import type {
  CarrierStaticInfo,
  CarrierName,
  PortStaticInfo,
  TransportVehicle,
  FerryTicketCategory,
} from '@libs/trip-planner-interfaces';

import {
  carriersCache,
  carriersNameCache,
  airportsCache,
  seaportsCache,
} from '../helpers/cache';

export class TransportStaticInfoSearchSercice {
  private flightEndPoint = new FlightEndpoint();
  private ferryEndpoint = new FerryEndpoint();

  public searchCarriersStaticInfo(
    carrierCodes: string[]
  ): Observable<CarrierStaticInfo[]> {
    const cachedResults: CarrierStaticInfo[] = [];
    const noCachedCarrierCodes: string[] = [];

    carrierCodes.forEach((carrierCode) => {
      const cachedResult = carriersCache.get(carrierCode);
      if (cachedResult) {
        cachedResults.push(cachedResult);
      } else {
        noCachedCarrierCodes.push(carrierCode);
      }
    });

    if (noCachedCarrierCodes.length === 0) {
      return of(cachedResults);
    }

    return this.flightEndPoint
      .searchCarriersStaticInfo(noCachedCarrierCodes)
      .pipe(
        tap((carriersStaticInfo) =>
          carriersStaticInfo.forEach((carrierStaticInfo) =>
            carriersCache.insert(carrierStaticInfo.code, carrierStaticInfo)
          )
        ),
        map((carriersStaticInfo) => [...cachedResults, ...carriersStaticInfo])
      );
  }

  public getCachedCarriers(carrierCodes: string[]): CarrierStaticInfo[] {
    return carrierCodes.reduce<CarrierStaticInfo[]>((acc, carrierCode) => {
      const cachedResult = carriersCache.get(carrierCode);
      if (cachedResult) {
        acc.push(cachedResult);
      }
      return acc;
    }, []);
  }

  public searchCarrierNames(
    carrierCodes: string[]
  ): Observable<Record<string, CarrierName>> {
    const noCachedCarrierCodes: string[] = [];
    const result: Record<string, CarrierName> = {};

    carrierCodes.forEach((carrierCode) => {
      const cachedResult = carriersNameCache.get(carrierCode);
      if (cachedResult) {
        result[carrierCode] = cachedResult;
      } else {
        noCachedCarrierCodes.push(carrierCode);
      }
    });

    if (noCachedCarrierCodes.length === 0) {
      return of(result);
    }

    return this.flightEndPoint.searchCarrierNames(noCachedCarrierCodes).pipe(
      map(({ results }) => {
        results.forEach((carrierName) => {
          carriersNameCache.insert(carrierName.code, carrierName);
        });

        const carrierNames: Record<string, CarrierName> = keyBy(
          results,
          'code'
        );

        Object.assign(result, carrierNames);
        return result;
      })
    );
  }

  public searchAirportStaticInfo(iata: string): Observable<PortStaticInfo> {
    const cachedResult = airportsCache.get(iata);
    if (cachedResult) {
      return of(cachedResult);
    }
    return this.flightEndPoint
      .searchAirportStaticInfo(iata)
      .pipe(
        tap((airportStaticInfo) =>
          airportsCache.insert(iata, airportStaticInfo)
        )
      );
  }

  public searchSeaportStaticInfo(portCode: string): Observable<PortStaticInfo> {
    const cachedResult = seaportsCache.get(portCode);
    if (cachedResult) {
      return of(cachedResult);
    }

    return this.ferryEndpoint
      .getSeaportStaticDataByCode(portCode)
      .pipe(
        tap((seaportStaticInfo) =>
          seaportsCache.insert(portCode, seaportStaticInfo)
        )
      );
  }

  public getFerryVehicleTypes(): Observable<TransportVehicle[]> {
    return this.ferryEndpoint.getFerryVehicleTypes();
  }

  public getFerryTicketCategories(
    key: string
  ): Observable<FerryTicketCategory[]> {
    return this.ferryEndpoint.getFerryTicketCategories(key);
  }
}
